// R116 #2 — "Text a vendor", now with GROUP texts (Jesse: "It would be great to
// be able to message vendors directly from the DASH. Yes, this can be done via
// Quo, but there's not really a way to create group in Quo like there is in
// WhatsApp."). Quo's API sends a multi-number `to` as ONE group thread (since
// 2026-06-16), so the office picks the vendor, taps team members who have a
// phone on file, types any other number, and the work order goes out as one
// group text from the vendor line — every reply (the vendor's or a teammate's)
// lands back on the work order.
//
// ONE component, two mounts:
//   openTextVendorModal(opts)       Maintenance — a body-mounted modal (a bottom
//                                   sheet on phones), z above the page chrome.
//   mountTextVendor(host, opts)     Calendar — inside ctx.openSheet's body (the
//                                   hub's own sheet look + sticky foot).
// Look: the Calendar stay panel — soft cards, small uppercase section labels
// with count chips, people as chips, muted helper text, ONE blue full-width
// primary, everything secondary behind the shared ⋯ Kebab.
//
// Rules the server enforces too (POST /api/assetops/vendor-flow/work-orders/:id/text):
// ≤ 9 people (Quo's 10 includes our line), E.164-valid, de-duplicated, never a
// guest's number; a teammate's phone is resolved on the server from their id
// (this page only ever sees the last 4 digits). Nothing here retries a send.
import { Formatters } from '../lib/formatters';
import { kebabHtml, bindKebabs, closeKebab } from './Kebab';

const API = '/api/assetops';
const GROUP_MAX_DEFAULT = 9;
const esc = (s: unknown): string => Formatters.escapeHtml(s == null ? '' : String(s));

export interface TextVendorOpts {
  /** The work order (id, title, listing_name, items, vendor_id). */
  wo: any;
  /** The vendor roster WITH phones (Maintenance has it); fetched when omitted. */
  vendors?: any[];
  /** Who is acting (rides the WO log). */
  by: string;
  /** After a send ('sent') or a link copy ('link') — the host reloads. */
  onDone?: (kind: 'sent' | 'link', result: any) => void | Promise<void>;
  /** Typed-but-unsent state (the Calendar's sheet asks before discarding). */
  onDirty?: (dirty: boolean) => void;
}
interface TeamMember { id: string; name: string; role?: string; last4?: string }

// Same rules as lib/quoGroups.js (the server re-checks everything).
export function normPhone(raw: string): string {
  const s = String(raw == null ? '' : raw).trim();
  if (!s) return '';
  const digits = s.replace(/[^\d+]/g, '');
  if (digits.startsWith('+')) return '+' + digits.slice(1).replace(/\D/g, '');
  const bare = digits.replace(/\D/g, '');
  if (bare.length === 10) return `+1${bare}`;
  if (bare.length === 11 && bare.startsWith('1')) return `+${bare}`;
  return bare ? `+${bare}` : '';
}
export function isE164(p: string): boolean {
  const s = String(p || '');
  if (s.startsWith('+1')) return /^\+1[2-9]\d{2}[2-9]\d{6}$/.test(s);
  return /^\+[2-9]\d{7,14}$/.test(s);
}
export function fmtPhone(p: string): string {
  const m = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(normPhone(p));
  return m ? `(${m[1]}) ${m[2]}-${m[3]}` : normPhone(p);
}
const initials = (name: string): string => {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  return ((parts[0] || '?')[0] + ((parts[1] || '')[0] || '')).toUpperCase();
};

const CSS_ID = 'tvx-css';
const TVX_CSS = `
.tvx-ovl { position:fixed; inset:0; z-index:9500; background:rgba(15,23,42,.38); display:flex; align-items:center; justify-content:center; padding:16px; box-sizing:border-box; }
.tvx-modal { width:580px; max-width:100%; max-height:calc(100vh - 32px); max-height:calc(100dvh - 32px); background:var(--card); border:1px solid var(--border); border-radius:var(--radius-lg); box-shadow:var(--shadow-pop); display:flex; flex-direction:column; overflow:hidden; }
.tvx-head { display:flex; align-items:center; gap:12px; padding:16px 18px 12px; border-bottom:1px solid var(--border-soft); }
.tvx-hic { flex:none; width:40px; height:40px; border-radius:var(--radius-sm); background:var(--accent-soft); color:var(--accent); display:inline-flex; align-items:center; justify-content:center; font-size:18px; }
.tvx-htxt { flex:1; min-width:0; }
.tvx-title { margin:0; font-size:16px; font-weight:700; color:var(--text); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.tvx-hsub { font-size:12px; color:var(--text-muted); margin-top:2px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.tvx-x { flex:none; width:36px; height:36px; border-radius:var(--radius-pill); border:none; background:transparent; color:var(--text-muted); font-size:16px; cursor:pointer; }
.tvx-x:hover { background:var(--bg-subtle); color:var(--text); }
.tvx-body { flex:1; overflow-y:auto; padding:4px 18px 0; overscroll-behavior:contain; }
.tvx-foot { position:sticky; bottom:0; display:flex; align-items:center; gap:8px; padding:12px 18px 14px; padding-bottom:calc(14px + env(safe-area-inset-bottom, 0px)); background:var(--card); border-top:1px solid var(--border-soft); }
.tvx-foot .tvx-send, .stc-sheet-foot .tvx-send { flex:1; justify-content:center; min-height:42px; font-size:13px; }
.stc-sheet-foot.tvx-sfoot { flex-wrap:nowrap; align-items:center; }
.tvx-foot .kb-btn, .stc-sheet-foot.tvx-sfoot .kb-btn { min-width:42px; min-height:42px; border:1px solid var(--border); background:var(--card); font-size:16px; }
.tvx-sec { font-size:11px; font-weight:700; letter-spacing:.09em; text-transform:uppercase; color:var(--text-muted); margin:18px 0 8px; display:flex; align-items:center; gap:8px; }
.tvx-sec:first-child { margin-top:12px; }
.tvx-count { display:inline-flex; align-items:center; justify-content:center; min-width:20px; height:20px; padding:0 7px; border-radius:var(--radius-pill); background:var(--bg-subtle); border:1px solid var(--border); color:var(--text); font-size:11px; letter-spacing:0; }
.tvx-count.full { background:var(--warning-soft); border-color:var(--warning-border, var(--warning)); color:var(--warning-text); }
.tvx-card { border:1px solid var(--border); border-radius:var(--radius); padding:12px 14px; background:var(--card); box-shadow:var(--shadow-card); }
.tvx-wo { display:flex; align-items:center; gap:12px; }
.tvx-wo-ic { flex:none; width:38px; height:38px; border-radius:var(--radius-pill); background:var(--info-soft); color:var(--info-text); display:inline-flex; align-items:center; justify-content:center; font-size:16px; }
.tvx-wo-txt { flex:1; min-width:0; }
.tvx-wo-t { font-size:13.5px; font-weight:600; color:var(--text); overflow-wrap:anywhere; }
.tvx-wo-s { font-size:12px; color:var(--text-muted); margin-top:2px; }
.tvx-pills { display:flex; flex-wrap:wrap; gap:6px; margin-top:10px; }
.tvx-pill { display:inline-flex; align-items:center; gap:6px; padding:4px 10px; border-radius:var(--radius-pill); font-size:11.5px; font-weight:600; white-space:nowrap; background:var(--bg-subtle); color:var(--text-muted); border:1px solid var(--border); max-width:100%; overflow:hidden; text-overflow:ellipsis; }
.tvx-pill-dot { width:6px; height:6px; border-radius:var(--radius-pill); background:var(--success); flex:none; }
.tvx-pill.warn { background:var(--warning-soft); color:var(--warning-text); border-color:var(--warning-border, var(--warning)); white-space:normal; }
.tvx-pill.warn .tvx-pill-dot { background:var(--warning); }
.tvx-lbl { display:block; font-size:11.5px; font-weight:600; color:var(--text-muted); margin:12px 0 6px; }
.tvx-lbl:first-child { margin-top:0; }
.tvx-lbl .tvx-muted { font-weight:400; color:var(--text-faint); }
.tvx select.input { min-height:40px; }
.tvx .input { min-height:38px; }
.tvx-chips { display:flex; flex-wrap:wrap; gap:6px; }
.tvx-chips:empty { display:none; }
.tvx .chip.tvx-person { min-height:36px; padding:4px 12px 4px 4px; gap:7px; max-width:100%; }
.tvx .chip.tvx-person[disabled] { opacity:.45; cursor:not-allowed; border-color:var(--border); }
.tvx-av { flex:none; width:26px; height:26px; border-radius:var(--radius-pill); background:var(--bg-subtle); border:1px solid var(--border); color:var(--text-muted); font-size:10.5px; font-weight:700; display:inline-flex; align-items:center; justify-content:center; }
.tvx .chip.active .tvx-av { background:var(--accent); border-color:var(--accent); color:#fff; }
.tvx-av.vendor { background:var(--accent-soft); border-color:var(--accent-border); color:var(--accent); }
.tvx-av.typed { font-size:12px; }
.tvx-nm { overflow:hidden; text-overflow:ellipsis; }
.tvx-l4 { color:var(--text-faint); font-size:11px; font-variant-numeric:tabular-nums; }
.tvx .chip.tvx-person.locked { cursor:default; border-color:var(--accent-border); background:var(--accent-soft); color:var(--accent-hover); }
.tvx-rm { flex:none; width:24px; height:24px; margin-right:-6px; border:none; border-radius:var(--radius-pill); background:transparent; color:var(--text-muted); cursor:pointer; font-size:12px; line-height:1; }
.tvx-rm:hover { background:var(--card); color:var(--danger-text); }
.tvx-add { display:flex; gap:8px; align-items:stretch; }
.tvx-add .input { flex:1; min-width:0; }
.tvx-add .btn { flex:none; min-height:38px; }
.tvx-err { margin-top:8px; padding:8px 10px; border-radius:var(--radius-sm); background:var(--danger-soft); color:var(--danger-text); font-size:12px; line-height:1.45; }
.tvx-help { font-size:11.5px; color:var(--text-faint); line-height:1.5; margin-top:10px; }
.tvx-help b { color:var(--text-muted); font-weight:600; }
.tvx-none { font-size:12px; color:var(--text-muted); line-height:1.5; padding:8px 10px; border:1px dashed var(--border); border-radius:var(--radius-sm); }
.tvx-row { display:flex; align-items:center; gap:10px; min-height:36px; font-size:12.5px; color:var(--text); border-top:1px solid var(--border-soft); cursor:pointer; }
.tvx-row:first-child { border-top:none; }
.tvx-row input { width:16px; height:16px; accent-color:var(--accent); flex:none; margin:0; }
.tvx-row .tvx-muted { color:var(--text-faint); font-size:11.5px; }
.tvx-row.sub { padding-left:26px; font-size:12px; color:var(--text-muted); min-height:32px; }
.tvx-seg { display:flex; width:100%; box-sizing:border-box; }
.tvx-seg .seg-tab { flex:1; justify-content:center; min-height:36px; white-space:normal; text-align:center; line-height:1.25; }
.tvx textarea.input { min-height:76px; resize:vertical; line-height:1.45; }
.tvx-note { margin:12px 0 2px; font-size:12px; line-height:1.45; padding:9px 11px; border-radius:var(--radius-sm); }
.tvx-note:empty { display:none; }
.tvx-note.ok { background:var(--success-soft); color:var(--success-text); }
.tvx-note.bad { background:var(--danger-soft); color:var(--danger-text); }
.tvx-note.busy { background:var(--bg-subtle); color:var(--text-muted); }
.tvx-load { padding:28px 0; text-align:center; font-size:12.5px; color:var(--text-muted); }
.tvx-spacer { height:14px; }
body.tvx-open .kb-pop { z-index:9600; }
@media (max-width: 640px) {
  .tvx-ovl { align-items:flex-end; padding:0; }
  .tvx-modal { width:100%; max-height:94vh; max-height:94dvh; border-radius:var(--radius-lg) var(--radius-lg) 0 0; }
  .tvx-body { padding:2px 16px 0; }
  .tvx-head { padding:14px 16px 10px; }
  .tvx-foot { padding-left:16px; padding-right:16px; }
  .tvx .input, .tvx select, .tvx textarea { font-size:16px; }
  .tvx .chip.tvx-person { min-height:40px; }
  .tvx-row { min-height:40px; }
}
`;
function ensureCss(): void {
  if (document.getElementById(CSS_ID)) return;
  const st = document.createElement('style'); st.id = CSS_ID; st.textContent = TVX_CSS; document.head.appendChild(st);
}

/** The form itself — renders into `body` (and its footer into `foot`); never re-renders typed fields. */
class TextVendorForm {
  private opts: TextVendorOpts;
  private body: HTMLElement;
  private foot: HTMLElement;
  private close: () => void;
  private info: any = null;
  private vendors: any[] = [];
  private team: TeamMember[] | null = null;
  private picked = new Set<string>();
  private extras: string[] = [];
  private request: 'quote' | 'work' = 'quote';
  private busy = false;
  private max = GROUP_MAX_DEFAULT;

  constructor(body: HTMLElement, foot: HTMLElement, opts: TextVendorOpts, close: () => void) {
    this.body = body; this.foot = foot; this.opts = opts; this.close = close;
  }

  async start(): Promise<void> {
    ensureCss();
    const w = this.opts.wo || {};
    this.body.innerHTML = `<div class="tvx"><div class="tvx-load">Loading the vendor line and your team…</div></div>`;
    this.foot.innerHTML = '';
    const [infoR, vendR] = await Promise.all([
      fetch(`${API}/vendor-flow/work-orders/${encodeURIComponent(String(w.id))}`).then(async r => ({ ok: r.ok, j: await r.json().catch(() => null) })).catch(() => ({ ok: false, j: null })),
      Array.isArray(this.opts.vendors) ? Promise.resolve({ ok: true, j: { vendors: this.opts.vendors } }) : fetch(`${API}/vendors`).then(async r => ({ ok: r.ok, j: await r.json().catch(() => null) })).catch(() => ({ ok: false, j: null })),
    ]);
    if (!this.body.isConnected) return;
    if (!infoR.ok || !infoR.j) {
      this.body.innerHTML = `<div class="tvx"><div class="tvx-note bad">Couldn't load the vendor flow for this work order${infoR.j && infoR.j.error ? ` — ${esc(infoR.j.error)}` : ''}. Close and try again.</div><div class="tvx-spacer"></div></div>`;
      return;
    }
    this.info = infoR.j;
    this.vendors = ((vendR.j && Array.isArray(vendR.j.vendors)) ? vendR.j.vendors : []).filter((v: any) => v && !v.archived);
    this.team = Array.isArray(this.info.team) ? this.info.team : null;
    this.max = Number(this.info.group_max) || GROUP_MAX_DEFAULT;
    this.draw();
  }

  private vendor(): any | null {
    const id = (this.body.querySelector<HTMLSelectElement>('[data-tv-vendor]') || { value: '' }).value;
    return id ? (this.vendors.find(v => String(v.id) === id) || null) : null;
  }
  private vendorPhone(): string {
    const v = this.vendor();
    const typed = (this.body.querySelector<HTMLInputElement>('[data-tv-phone]') || { value: '' }).value.trim();
    return normPhone(typed || (v && v.phone) || '');
  }
  private count(): number { return (this.vendorPhone() ? 1 : 0) + this.picked.size + this.extras.length; }

  private draw(): void {
    const w = this.opts.wo || {};
    const info = this.info;
    const line = info.quo && info.quo.line;
    const quoOff = !(info.quo && info.quo.configured);
    const lineBad = !!(line && !line.matched);
    const items: any[] = Array.isArray(w.items) ? w.items : [];
    const cands: any[] = Array.isArray(info.candidates) ? info.candidates : [];
    const vid = String(w.vendor_id || '');
    const pills = [
      line && line.matched ? `<span class="tvx-pill"><span class="tvx-pill-dot"></span>From ${esc(fmtPhone(line.number || ''))}${line.name ? ` · ${esc(line.name)}` : ''}</span>` : '',
      quoOff ? '<span class="tvx-pill warn"><span class="tvx-pill-dot"></span>Quo isn\'t connected on this server — you can still copy the link (⋯)</span>' : '',
      lineBad ? '<span class="tvx-pill warn"><span class="tvx-pill-dot"></span>The vendor line isn\'t in this Quo workspace — texts are refused; copy the link (⋯)</span>' : '',
      w.vendor_link ? '<span class="tvx-pill">Has a vendor link</span>' : '',
    ].filter(Boolean).join('');
    this.body.innerHTML = `<div class="tvx">
      <div class="tvx-sec">Work order</div>
      <div class="tvx-card">
        <div class="tvx-wo"><span class="tvx-wo-ic" aria-hidden="true">🔧</span><div class="tvx-wo-txt"><div class="tvx-wo-t">${esc(w.title || 'Work order')}</div><div class="tvx-wo-s">${esc(w.listing_name || '')}</div></div></div>
        ${pills ? `<div class="tvx-pills">${pills}</div>` : ''}
      </div>

      <div class="tvx-sec">Who gets it <span class="tvx-count" data-tv-count></span></div>
      <div class="tvx-card">
        <label class="tvx-lbl" for="tv-vendor-${esc(w.id)}">Vendor</label>
        <select class="input" id="tv-vendor-${esc(w.id)}" data-tv-vendor>
          <option value="">— type the vendor's number below —</option>
          ${this.vendors.map(v => `<option value="${esc(v.id)}" ${String(v.id) === vid ? 'selected' : ''}>${esc(v.name)}${v.phone ? ` · ${esc(fmtPhone(v.phone))}` : ' · no phone on file'}</option>`).join('')}
        </select>
        <div data-tv-phonewrap>
          <label class="tvx-lbl" for="tv-phone-${esc(w.id)}">Vendor's phone <span class="tvx-muted">(if not on file)</span></label>
          <input class="input" id="tv-phone-${esc(w.id)}" data-tv-phone type="tel" inputmode="tel" autocomplete="off" placeholder="(619) 555-0123">
        </div>
        <div class="tvx-lbl">Add your team <span class="tvx-muted">— people with a phone on file</span></div>
        <div data-tv-team></div>
        <label class="tvx-lbl" for="tv-extra-${esc(w.id)}">Add another number</label>
        <div class="tvx-add"><input class="input" id="tv-extra-${esc(w.id)}" data-tv-extra type="tel" inputmode="tel" autocomplete="off" placeholder="(619) 555-0123 or +44…"><button type="button" class="btn btn-secondary" data-tv-addnum>＋ Add</button></div>
        <div class="tvx-err" data-tv-extraerr role="alert" hidden></div>
        <div class="tvx-lbl" data-tv-tolbl>On this text</div>
        <div class="tvx-chips" data-tv-people></div>
        <div class="tvx-help" data-tv-help></div>
      </div>

      <div class="tvx-sec">What you're asking for</div>
      <div class="seg-tabs tvx-seg" role="tablist">
        <button type="button" class="seg-tab ${this.request === 'quote' ? 'active' : ''}" data-tv-req="quote" role="tab" aria-selected="${this.request === 'quote'}">A quote first</button>
        <button type="button" class="seg-tab ${this.request === 'work' ? 'active' : ''}" data-tv-req="work" role="tab" aria-selected="${this.request === 'work'}">Do the work</button>
      </div>
      <div class="tvx-help" data-tv-reqhelp>${this.reqHelp()}</div>

      ${items.length ? `<div class="tvx-sec">Items <span class="tvx-count" data-tv-itemcount>${items.length}/${items.length}</span></div>
      <div class="tvx-card">
        ${items.map((i: any) => `<label class="tvx-row"><input type="checkbox" data-tv-item="${esc(w.id)}|${esc(i.key)}" checked><span>${esc(i.title)}${i.done ? ' <span class="tvx-muted">(done)</span>' : ''}</span></label>`).join('')}
        <div class="tvx-help">Untick what the vendor shouldn't quote or touch.</div>
      </div>` : ''}

      ${cands.length ? `<div class="tvx-sec">Bundle other open work <span class="tvx-count">${cands.length}</span></div>
      <div class="tvx-card">
        ${cands.map((c: any) => `<label class="tvx-row"><input type="checkbox" data-tv-bundle="${esc(c.id)}"><span>${esc(c.title)} <span class="tvx-muted">· ${esc(c.priority)}${c.items ? ` · ${esc(c.items)} item${Number(c.items) === 1 ? '' : 's'}` : ''}</span></span></label>${(c.item_list || []).map((i: any) => `<label class="tvx-row sub"><input type="checkbox" data-tv-item="${esc(c.id)}|${esc(i.key)}" checked><span>${esc(i.title)}${i.done ? ' (done)' : ''}</span></label>`).join('')}`).join('')}
        <div class="tvx-help">One link, one visit — the same property only.</div>
      </div>` : ''}

      <div class="tvx-sec">Message</div>
      <textarea class="input" data-tv-msg rows="3" maxlength="1400" placeholder="Leave blank for the standard message" aria-label="Message"></textarea>
      <div class="tvx-help">The vendor link is added at the end automatically.</div>
      <div class="tvx-note" data-tv-note role="status" aria-live="polite"></div>
      <div class="tvx-spacer"></div>
    </div>`;
    const kb = kebabHtml([
      { label: 'Just create / copy the link', icon: '🔗', attrs: { 'data-tv-copylink': '' }, title: 'Mint the no-login link without texting — send it however you like' },
    ], { tip: 'More', attrs: { 'aria-label': 'More options' } });
    this.foot.innerHTML = `<button type="button" class="btn btn-primary tvx-send" data-tv-send ${quoOff || lineBad ? 'disabled' : ''}>Send text</button>${kb}`;
    bindKebabs(this.foot);
    this.drawPeople();
    this.bind();
  }

  private reqHelp(): string {
    return this.request === 'quote' ? 'They look at the details and photos and send a price — or ask for a site visit.' : 'They get the checklist, mark progress with before / after photos, and send their invoice.';
  }

  /** The people block only (chips, count, button label) — typed fields elsewhere keep their values. */
  private drawPeople(): void {
    const v = this.vendor();
    const vp = this.vendorPhone();
    const n = this.count();
    const full = n >= this.max;
    const wrap = this.body.querySelector<HTMLElement>('[data-tv-phonewrap]');
    if (wrap) wrap.hidden = !!(v && v.phone);
    const teamEl = this.body.querySelector<HTMLElement>('[data-tv-team]');
    if (teamEl) {
      if (this.team === null) teamEl.innerHTML = '<div class="tvx-none">Couldn\'t load your team\'s phones — you can still type numbers below.</div>';
      else if (!this.team.length) teamEl.innerHTML = '<div class="tvx-none">No one on the team has a phone on file yet. Add mobiles on <b>Users &amp; Roles</b> to put people on vendor texts.</div>';
      else teamEl.innerHTML = `<div class="tvx-chips">${this.team.map(t => {
        const on = this.picked.has(t.id);
        return `<button type="button" class="chip tvx-person ${on ? 'active' : ''}" data-tv-team-id="${esc(t.id)}" aria-pressed="${on}" ${!on && full ? 'disabled title="A group text holds up to ' + this.max + ' people"' : ''}><span class="tvx-av">${esc(initials(t.name))}</span><span class="tvx-nm">${esc(t.name)}</span>${t.last4 ? `<span class="tvx-l4">··${esc(t.last4)}</span>` : ''}</button>`;
      }).join('')}</div>`;
    }
    const peopleEl = this.body.querySelector<HTMLElement>('[data-tv-people]');
    if (peopleEl) {
      const vendorChip = vp ? `<span class="chip tvx-person locked" title="The vendor"><span class="tvx-av vendor">${esc(initials((v && v.name) || 'V'))}</span><span class="tvx-nm">${esc((v && v.name) || fmtPhone(vp))}</span><span class="tvx-l4">vendor</span></span>` : '';
      const teamChips = (this.team || []).filter(t => this.picked.has(t.id)).map(t => `<span class="chip tvx-person active"><span class="tvx-av">${esc(initials(t.name))}</span><span class="tvx-nm">${esc(t.name)}</span><button type="button" class="tvx-rm" data-tv-unpick="${esc(t.id)}" aria-label="Take ${esc(t.name)} off this text">✕</button></span>`).join('');
      const extraChips = this.extras.map(p => `<span class="chip tvx-person active"><span class="tvx-av typed" aria-hidden="true">#</span><span class="tvx-nm">${esc(fmtPhone(p))}</span><button type="button" class="tvx-rm" data-tv-rmnum="${esc(p)}" aria-label="Remove ${esc(fmtPhone(p))}">✕</button></span>`).join('');
      peopleEl.innerHTML = vendorChip + teamChips + extraChips || '<div class="tvx-none">Pick a vendor or type their number.</div>';
    }
    const cnt = this.body.querySelector<HTMLElement>('[data-tv-count]');
    if (cnt) { cnt.textContent = `${n} / ${this.max}`; cnt.classList.toggle('full', full); cnt.title = full ? `Quo's limit: ${this.max} people plus our line` : ''; }
    const help = this.body.querySelector<HTMLElement>('[data-tv-help]');
    if (help) help.innerHTML = n > 1
      ? `<b>One group text</b> from the vendor line to all ${n} — every reply lands on this work order. Everyone sees each other's number and every reply. Up to ${this.max} people.`
      : `Add teammates or another number to make it <b>one group text</b> (up to ${this.max} people) — replies from anyone on it land on this work order.`;
    const add = this.body.querySelector<HTMLButtonElement>('[data-tv-addnum]');
    if (add) add.disabled = full;
    const send = this.foot.querySelector<HTMLButtonElement>('[data-tv-send]');
    if (send && !this.busy) send.textContent = n > 1 ? `Send group text · ${n} people` : 'Send text';
  }

  private bind(): void {
    const b = this.body;
    const dirty = () => this.opts.onDirty && this.opts.onDirty(true);
    b.querySelector('[data-tv-vendor]')?.addEventListener('change', () => {
      // a vendor with a number on file hides the phone box — never leave a stale typed number behind it
      const v = this.vendor(); const ph = b.querySelector<HTMLInputElement>('[data-tv-phone]');
      if (v && v.phone && ph) ph.value = '';
      this.drawPeople(); dirty();
    });
    b.querySelector('[data-tv-phone]')?.addEventListener('input', () => { this.drawPeople(); dirty(); });
    b.addEventListener('click', (e) => {
      const t = e.target as HTMLElement;
      const tm = t.closest<HTMLElement>('[data-tv-team-id]');
      if (tm && !(tm as HTMLButtonElement).disabled) {
        const id = tm.dataset.tvTeamId || '';
        if (this.picked.has(id)) this.picked.delete(id);
        else if (this.count() < this.max) this.picked.add(id);
        this.drawPeople(); dirty(); return;
      }
      const un = t.closest<HTMLElement>('[data-tv-unpick]');
      if (un) { this.picked.delete(un.dataset.tvUnpick || ''); this.drawPeople(); return; }
      const rm = t.closest<HTMLElement>('[data-tv-rmnum]');
      if (rm) { this.extras = this.extras.filter(p => p !== rm.dataset.tvRmnum); this.drawPeople(); return; }
      if (t.closest('[data-tv-addnum]')) { this.addExtra(); return; }
      const rq = t.closest<HTMLElement>('[data-tv-req]');
      if (rq) {
        this.request = rq.dataset.tvReq === 'work' ? 'work' : 'quote';
        b.querySelectorAll<HTMLElement>('[data-tv-req]').forEach(x => { const on = x.dataset.tvReq === this.request; x.classList.toggle('active', on); x.setAttribute('aria-selected', String(on)); });
        const h = b.querySelector<HTMLElement>('[data-tv-reqhelp]'); if (h) h.textContent = this.reqHelp();
        return;
      }
    });
    b.querySelector<HTMLInputElement>('[data-tv-extra]')?.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); this.addExtra(); } });
    b.querySelector<HTMLInputElement>('[data-tv-extra]')?.addEventListener('input', () => { const er = b.querySelector<HTMLElement>('[data-tv-extraerr]'); if (er) er.hidden = true; });
    b.querySelector('[data-tv-msg]')?.addEventListener('input', dirty);
    b.addEventListener('change', (e) => { const t = e.target as HTMLElement; if (t.matches('[data-tv-item], [data-tv-bundle]')) { this.itemCount(); dirty(); } });
    this.foot.querySelector('[data-tv-send]')?.addEventListener('click', () => { void this.send(); });
    // the ⋯ menu moves into a popover on open — its item keeps this listener
    this.foot.querySelector('[data-tv-copylink]')?.addEventListener('click', () => { void this.copyLink(); });
  }

  private itemCount(): void {
    const w = this.opts.wo || {};
    const mine = Array.from(this.body.querySelectorAll<HTMLInputElement>(`[data-tv-item^="${CSS.escape(String(w.id))}|"]`));
    const el = this.body.querySelector<HTMLElement>('[data-tv-itemcount]');
    if (el) el.textContent = `${mine.filter(x => x.checked).length}/${mine.length}`;
  }

  private addExtra(): void {
    const inp = this.body.querySelector<HTMLInputElement>('[data-tv-extra]');
    const er = this.body.querySelector<HTMLElement>('[data-tv-extraerr]');
    if (!inp) return;
    const raw = inp.value.trim();
    const fail = (m: string) => { if (er) { er.textContent = m; er.hidden = false; } inp.focus(); };
    if (!raw) return fail('Type a phone number first.');
    const p = normPhone(raw);
    if (!isE164(p)) return fail(`"${raw.slice(0, 30)}" isn't a phone number — use 10 digits like (619) 555-0123, or + and the country code for a number abroad.`);
    if (p === this.vendorPhone() || this.extras.includes(p)) return fail(`${fmtPhone(p)} is already on this text.`);
    if (this.count() >= this.max) return fail(`A group text holds up to ${this.max} people (Quo's limit).`);
    this.extras.push(p);
    inp.value = '';
    if (er) er.hidden = true;
    this.drawPeople();
    this.opts.onDirty && this.opts.onDirty(true);
  }

  private payload(): any {
    const w = this.opts.wo || {};
    const b = this.body;
    const v = this.vendor();
    const phone = (b.querySelector<HTMLInputElement>('[data-tv-phone]') || { value: '' }).value.trim();
    const item_keys: Record<string, string[]> = {};
    b.querySelectorAll<HTMLInputElement>('[data-tv-item]').forEach(el => { const [wid, key] = String(el.dataset.tvItem || '').split('|'); if (!item_keys[wid]) item_keys[wid] = []; if (el.checked) item_keys[wid].push(key); });
    return {
      vendor_id: v ? v.id : undefined, to_phone: phone || undefined,
      request: this.request,
      message: (b.querySelector<HTMLTextAreaElement>('[data-tv-msg]') || { value: '' }).value.trim() || undefined,
      bundle_wo_ids: Array.from(b.querySelectorAll<HTMLInputElement>('[data-tv-bundle]:checked')).map(el => el.dataset.tvBundle),
      item_keys,
      team_ids: [...this.picked],
      to_phones: this.extras.slice(),
      by: this.opts.by || '',
      _wo: String(w.id),
    };
  }
  private note(text: string, tone: 'ok' | 'bad' | 'busy' = 'ok'): void {
    const n = this.body.querySelector<HTMLElement>('[data-tv-note]');
    if (!n) return;
    n.textContent = text; n.className = `tvx-note ${text ? tone : ''}`;
    if (text) n.scrollIntoView({ block: 'nearest' });
  }
  private scopeOk(): boolean {
    const w = this.opts.wo || {};
    const mine = Array.from(this.body.querySelectorAll<HTMLInputElement>(`[data-tv-item^="${CSS.escape(String(w.id))}|"]`));
    if (mine.length && !mine.some(el => el.checked)) { this.note('Tick at least one item on this work order (or all of them) — the vendor needs something to quote or do.', 'bad'); return false; }
    return true;
  }
  private droppedNote(j: any): string {
    const d: string[] = j && Array.isArray(j.dropped_wo_ids) ? j.dropped_wo_ids : [];
    if (!d.length) return '';
    const cands: any[] = (this.info && this.info.candidates) || [];
    return ` Left out (no items ticked): ${d.map(id => (cands.find(c => String(c.id) === String(id)) || {}).title || id).join(', ')}.`;
  }
  private async post(path: string, body: any): Promise<{ ok: boolean; j: any }> {
    const { _wo, ...rest } = body;
    try {
      const r = await fetch(`${API}/vendor-flow/work-orders/${encodeURIComponent(String(_wo))}/${path}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(rest) });
      const j = await r.json().catch(() => ({}));
      return { ok: r.ok && !!(j && j.ok), j };
    } catch { return { ok: false, j: { error: 'No connection — nothing was sent. Try again.' } }; }
  }

  private async send(): Promise<void> {
    if (this.busy || !this.scopeOk()) return;
    if (!this.vendorPhone()) { this.note('Pick a vendor with a phone number, or type their number.', 'bad'); return; }
    const pending = (this.body.querySelector<HTMLInputElement>('[data-tv-extra]') || { value: '' }).value.trim();
    if (pending) { this.note(`Add or clear the number you typed (“${pending.slice(0, 30)}”) first.`, 'bad'); return; }
    const n = this.count();
    const btn = this.foot.querySelector<HTMLButtonElement>('[data-tv-send]');
    this.busy = true;
    if (btn) { btn.disabled = true; btn.textContent = n > 1 ? `Sending to ${n} people…` : 'Sending…'; }
    this.note('');
    const { ok, j } = await this.post('text', this.payload());
    this.busy = false;
    if (btn) btn.disabled = false;
    this.drawPeople();
    if (!ok) { this.note(`Not sent — ${(j && j.error) || 'try again'}`, 'bad'); return; }
    const who = j.group ? `${j.group.n} people as one group text` : fmtPhone(j.to || '');
    this.note(`Sent to ${who} from ${fmtPhone(j.from || '')}.${this.droppedNote(j)}`, 'ok');
    this.opts.onDirty && this.opts.onDirty(false);
    try { await (this.opts.onDone && this.opts.onDone('sent', j)); } catch { /* the host's reload is best-effort */ }
    setTimeout(() => { if (this.body.isConnected) this.close(); }, 1600);
  }

  private async copyLink(): Promise<void> {
    closeKebab();
    if (this.busy || !this.scopeOk()) return;
    this.busy = true;
    this.note('Creating the link…', 'busy');
    const { ok, j } = await this.post('link', this.payload());
    this.busy = false;
    if (!ok) { this.note(`Not created — ${(j && j.error) || 'try again'}`, 'bad'); return; }
    let copied = false;
    try { await navigator.clipboard.writeText(j.url); copied = true; } catch { /* shown below to copy by hand */ }
    this.note(`${copied ? 'Link copied' : 'Link'}: ${j.url}${this.droppedNote(j)}`, 'ok');
    try { await (this.opts.onDone && this.opts.onDone('link', j)); } catch { /* best-effort */ }
  }
}

/** Maintenance: a body-mounted modal (a bottom sheet on phones). Esc / backdrop / ✕ close it. */
export function openTextVendorModal(opts: TextVendorOpts): () => void {
  ensureCss();
  document.getElementById('mn-tv-modal')?.remove();
  const w = opts.wo || {};
  const ovl = document.createElement('div');
  ovl.id = 'mn-tv-modal';
  ovl.className = 'tvx-ovl';
  ovl.innerHTML = `<div class="tvx-modal" role="dialog" aria-modal="true" aria-labelledby="tvx-title">
    <div class="tvx-head"><span class="tvx-hic" aria-hidden="true">📲</span><div class="tvx-htxt"><h3 class="tvx-title" id="tvx-title">Text a vendor</h3><div class="tvx-hsub">Every reply lands on this work order</div></div><button type="button" class="tvx-x" data-tv-close aria-label="Close">✕</button></div>
    <div class="tvx-body"></div>
    <div class="tvx-foot" data-bottom-bar></div>
  </div>`;
  document.body.appendChild(ovl);
  document.body.classList.add('tvx-open');
  const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !document.querySelector('.kb-pop')) close(); };
  const close = () => { closeKebab(); ovl.remove(); document.body.classList.remove('tvx-open'); document.removeEventListener('keydown', onKey); };
  ovl.addEventListener('click', (e) => { if (e.target === ovl) close(); });
  ovl.querySelector('[data-tv-close]')?.addEventListener('click', close);
  document.addEventListener('keydown', onKey);
  const form = new TextVendorForm(ovl.querySelector<HTMLElement>('.tvx-body')!, ovl.querySelector<HTMLElement>('.tvx-foot')!, opts, close);
  void form.start();
  (ovl.querySelector<HTMLElement>('[data-tv-close]'))?.focus();
  return close;
}

/** Calendar: render inside a host sheet body (ctx.openSheet) — the footer is the hub's sticky .stc-sheet-foot. */
export function mountTextVendor(host: HTMLElement, opts: TextVendorOpts & { close: () => void }): void {
  ensureCss();
  host.innerHTML = '<div data-tvx-body></div><div class="stc-sheet-foot tvx-sfoot" data-bottom-bar></div>';
  const form = new TextVendorForm(host.querySelector<HTMLElement>('[data-tvx-body]')!, host.querySelector<HTMLElement>('.tvx-sfoot')!, opts, opts.close);
  void form.start();
}
